import { format, formatDistance, subDays } from 'date-fns';

import AnalyzeService from './AnalyzeService';
import { NewUser } from '../../types/UserManagement';
import { Skill } from '@/client/types/Skill';
import type { User as UserType } from '@/client/types/User';
import { learnApiClient } from './clients/learnApiClient';
import { learnV4ApiClient } from './clients/learnV4ApiClient';

// TODO: Remove getSuggestions
// TODO: Remove getComments
// TODO: Remove getCommentCount
// TODO: Remove getUserContentViews

const createUser = async (user: NewUser): Promise<NewUser> => {
  const response = await learnApiClient.post(`users`, {
    ...user,
  });
  return response.data;
};

const updateUser = async (id: string, updateBody: NewUser): Promise<NewUser> => {
  const response = await learnApiClient.post(`users/${id}`, {
    ...updateBody,
  });
  return response.data;
};

const getUser = async (id: string | undefined): Promise<UserType> => {
  const response = await learnApiClient.get(
    `users/${id}?fields=first_name,last_name,full_name,email,role,image,is_manager,is_collaborator,metadata,banner_image,manager_email,buddy_email,managing_groups,access_profile,skills,social_links,about,hired,job_title,linked_departments,linked_locations,provisioning_status,language&skills-populate=name&linked_locations-populate=name&linked_departments-populate=name&company-populate=user_metadata_fields`,
  );
  return response.data;
};

const getUserForEditor = async (id: string | undefined): Promise<NewUser> => {
  const response = await learnV4ApiClient.get(
    `admin-users/${id}?fields=first_name,last_name,userid,full_name,email,role,image,is_manager,is_collaborator,is_purchaser,manager_email,buddy_email,managing_groups,manager_configuration,access_profile,hired,createdAt,last_login,job_title,linked_departments,linked_locations,linked_teams,linked_org_level,linked_grade,groups,provisioning_status,language,partner,metadata,partner_role,address,country,distributor,region,group&company-populate=user_metadata_fields,`,
  );
  return response.data;
};

const updateBannerImage = async (id: string, banner_image: string) =>
  learnApiClient.post(`users/${id}?fields=banner_image`, {
    banner_image,
  });

const updateAvatar = async (id: string, image: string) => {
  const response = await learnApiClient.post(`users/${id}?fields=image`, {
    image,
  });

  return response.data.image;
};

const updateSocialProfiles = (id: string, social_links: { name: string; value: string }[]) =>
  learnApiClient.post(`users/${id}?fields=social_links`, { social_links });

const updateSkills = (skills: Skill[], userId: string) => {
  const skillIds = skills.map((skill) => skill._id);

  const response = learnApiClient.post(
    `users/${userId}?fields=first_name,last_name,full_name,email,role,is_collaborator,image,banner_image,skills,social_links,about,provisioning_status,language&skills-populate=name`,
    {
      skills: skillIds,
    },
  );

  return response;
};

const updateDetails = async (
  id: string,
  about: string,
  first_name: string,
  language: string,
  last_name: string,
) => {
  const response = await learnApiClient.post(
    `users/${id}?fields=first_name,last_name,about,language`,
    {
      about,
      first_name,
      language,
      last_name,
    },
  );
  return response.data;
};

const getCommentCount = async (_id: string | undefined) => {
  const response = await learnApiClient.get(`maincomments/count?user=${_id}`);

  return response.data;
};

const getComments = async (_id: string | undefined) => {
  const response = await learnApiClient.get(
    `maincomments?article-populate=title&assessment-populate=title&event-populate=title&fields=article,media,track,presentation,imported_content,assessment,event,createdAt,user&imported_content-populate=title&media-populate=title,type&presentation-populate=title&sort=createdAt,-1&track-populate=name&user=${_id}`,
  );

  return response.data;
};

const getSuggestions = async (_id: string | undefined) => {
  const response = await learnApiClient.get(
    `suggestions?page=1&per_page=1&article-populate=title&assessment-populate=title&event-populate=title&fields=article,media,track,presentation,imported_content,assessment,event,createdAt,user&imported_content-populate=title&media-populate=title,type&presentation-populate=title&sort=createdAt,-1&track-populate=name&user=${_id}`,
  );

  return response.data;
};

const updateCustomFields = async (userId: string, metadata: any) =>
  learnApiClient.post(`users/${userId}?fields=metadata`, { metadata });

type ManagerConfiguration = {
  allow_direct_report_suspension: boolean;
  disable_assignments: boolean;
  disable_assignment_completion: boolean;
  disable_assignment_removal: boolean;
  disable_assignment_edit: boolean;
  allow_user_creation: boolean;
  allow_user_edit: boolean;
  restricted_user_edit: boolean;
  restricted_editable_fields: string[];
  allow_adding_group_manager: boolean;
  segmentation_required: string[];
  segmentation_subset: string[];
  hide_options: string[];
  hide_non_managing_groups: boolean;
  allow_password_reset: boolean;
};

const getManagerConfiguration = async (userId: string): Promise<ManagerConfiguration> => {
  const response = await learnV4ApiClient.get(`admin-users/${userId}?fields=manager_configuration`);

  return response.data;
};

const getUserActivityFeed = async (userId: string) => {
  const commonQueryParams =
    'article-populate=title&assessment-populate=title&event-populate=title&fields=article,media,track,presentation,imported_content,assessment,event,createdAt,user&imported_content-populate=title&media-populate=title,type&presentation-populate=title&sort=createdAt,-1&track-populate=name';

  const [
    userSuggestionsResponse,
    userCommentsResponse,
    userCommentCountResponse,
    userContentViewsResponse,
  ] = await Promise.all([
    learnApiClient.get(`suggestions?page=1&per_page=1&${commonQueryParams}&user=${userId}`),
    learnApiClient.get(`maincomments?${commonQueryParams}&user=${userId}`),
    learnApiClient.get(`maincomments/count?user=${userId}`),
    AnalyzeService.getUserContentViews(userId, format(subDays(new Date(), 30), 'MM-dd-yy')),
  ]);

  const setSocialFeedItems = (suggestions: any[], comments: any[]) => {
    const socialFeedItems: any[] = [];

    const formatFeedItems = (arr: any[], actionType: string) => {
      arr.forEach((item) => {
        // Find the content type by checking which property exists
        const contentTypes = [
          'article',
          'media',
          'track',
          'presentation',
          'imported_content',
          'assessment',
          'event',
        ];

        const foundType = contentTypes.find((type) => item[type]);
        if (foundType) {
          const content = item[foundType];
          socialFeedItems.push({
            _id: content._id,
            type: foundType === 'media' ? content.type : foundType,
            title: foundType === 'track' ? content.name : content.title,
            actionType,
            createdAt: item.createdAt,
            timeSince: formatDistance(new Date(item.createdAt), new Date()),
          });
        }
      });
    };

    formatFeedItems(comments, 'comment');
    formatFeedItems(suggestions, 'like');

    return socialFeedItems
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      .slice(0, 10);
  };

  return {
    activityFeed: setSocialFeedItems(userSuggestionsResponse.data, userCommentsResponse.data),
    userCommentCount: userCommentCountResponse.data.count,
    userContentViews: userContentViewsResponse.count,
  };
};

const User = {
  createUser,
  updateUser,
  getUser,
  getUserForEditor,
  updateBannerImage,
  updateAvatar,
  updateSocialProfiles,
  updateSkills,
  updateDetails,
  getCommentCount,
  getComments,
  getSuggestions,
  updateCustomFields,
  getManagerConfiguration,
  getUserActivityFeed,
};

export default User;
